import { Button, Divider, Typography } from "@drone-js/core";
import { Fragment, useMemo, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import type {
    OneOnboardingRoom,
    PropertyData,
    RoomCaptionDropdownItem,
} from "../../../libs/codegen/property-onboarding";
import { VStack } from "../../commons/components/Stack.tsx";
import { useOnboardingPageContextAtomValue } from "../onboardingPageContextAtom.ts";
import { useStaticDataAtomValue } from "../staticDataAtom.ts";
import { PhotosController } from "./PhotosController.tsx";

export const RoomPhotosController = () => {
    const [expand, setExpand] = useState(false);
    const { control, watch } = useFormContext<PropertyData>();
    const { fields } = useFieldArray({
        control,
        name: "rooms",
    });
    const {
        cmsData: {
            photo: { addPhotos, roomPhotos, addPhotosNowOrLater, unitBedroomBathroom },
        },
        roomCaptionItems,
    } = useStaticDataAtomValue();
    const pageContext = useOnboardingPageContextAtomValue();
    const additionalRoomCaptionItems = pageContext?.additionalRoomCaptionItems;

    const rooms = watch("rooms") as OneOnboardingRoom[];

    // Merge room caption items from static data with additionalRoomCaptionItems from page context
    const allRoomCaptionItems: RoomCaptionDropdownItem[] = useMemo(() => {
        const uniqueItems = new Map<number, RoomCaptionDropdownItem>();

        // First add static room caption items
        roomCaptionItems?.forEach((item) => {
            if (item.id != null && item.name) {
                uniqueItems.set(item.id, item);
            }
        });

        // Then add additional room caption items (from page context)
        // These will only override if ID doesn't already exist
        additionalRoomCaptionItems?.forEach((item) => {
            if (item.id != null && item.name && !uniqueItems.has(item.id)) {
                uniqueItems.set(item.id, item);
            }
        });

        return Array.from(uniqueItems.values());
    }, [roomCaptionItems, additionalRoomCaptionItems]);

    const getRoomCaptionName = (captionId: number) => {
        const caption = allRoomCaptionItems.find((item) => item.id === captionId);
        return caption?.name;
    };

    if (!expand)
        return (
            <Fragment>
                <VStack gap="8">
                    <Typography variant="heading-l" textColor="base.default">
                        {roomPhotos}
                    </Typography>
                    <Typography variant="body-s" textColor="base.subtle">
                        {addPhotosNowOrLater}
                    </Typography>
                </VStack>
                <Button
                    data-element-name="expand-room-photos"
                    variant="label"
                    hierarchy="tertiary"
                    width="parent-fit"
                    size="m"
                    leadingIcon="fill.arrow.thin-dropdown"
                    label={addPhotos}
                    onClick={() => setExpand(true)}
                />
            </Fragment>
        );

    return fields.map((field, i) => (
        <VStack key={field.id} gap="24">
            <VStack gap="8">
                <Typography variant="heading-l" textColor="base.default">
                    {getRoomCaptionName(rooms[i]?.captionId)}
                </Typography>
                <Typography variant="body-s" textColor="base.subtle">
                    {unitBedroomBathroom}
                </Typography>
            </VStack>
            <PhotosController
                fieldPrefix={`rooms.${i}`}
                roomPhotos
                roomId={rooms[i]?.id}
                roomCaptionName={getRoomCaptionName(rooms[i]?.captionId)}
            />
            {i < fields.length - 1 && <Divider />}
        </VStack>
    ));
};
